/* eslint-disable react/no-array-index-key */
import React from 'react';

import {Formatters, useFormatters} from './light';
import {BreadcrumbItem, LightBreadcrumbs} from './LightBreadcrumbs';
import {HaidiSchema} from '../types';
import {TranslateUtils} from '@wallaby-ui/core';
import {useIntl} from 'react-intl';

type EncounterEventType = HaidiSchema.components['schemas']['EncounterPropertiesDto']['encounterType'];
type IconDef = {
  iconName: string;
  iconViewBox: string;
};
export type PatientProp = {
  isLoading: boolean;
  id: string | number | undefined;
  name: string | undefined;
  birthNumber: string | undefined;
};
export type EncounterProp = {
  isLoading: boolean;
  id: string | undefined;
  encounterType: EncounterEventType | undefined;
  orgShortName: string | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  isDeleted: boolean | undefined;
  isEscort: boolean | undefined;
};
export type DocumentProp = {
  isLoading: boolean;
  id: string | undefined;
  name: string | undefined;
  timestamp: string | undefined;
};
type Props = {
  patient?: PatientProp;
  encounter?: EncounterProp;
  document?: DocumentProp;
};
export function formatPatientBreadcrumb(patient: PatientProp, intl): string {
  return patient.isLoading
    ? '...'
    : `${patient.name} ${
        patient.birthNumber ? `(${patient.birthNumber})` : `(${TranslateUtils.translate('patient.list.noDataToShow', intl)})`
      }`;
}
export function formatEncounterBreadcrumb(encounter: EncounterProp, formatters: Formatters): string {
  const {t, formatDate} = formatters;
  const startDate = formatDate(encounter.startDate ?? '');
  let endDate = encounter.endDate ? formatDate(encounter.endDate) : t('case.encounter.inProgress');
  if (encounter.isDeleted) endDate = t('case.encounter.deleted');
  const dateString = encounter.encounterType === 'OUTPATIENT' ? `${endDate}` : `${startDate} - ${endDate}`;
  let extraBracket = '';
  if (encounter.isEscort) extraBracket = ` (${t('case.encounter.escort')})`;
  return encounter.isLoading ? '...' : `${encounter.orgShortName ?? '?'}, ${dateString}${extraBracket}`;
}
export function formatDocumentBreadcrumb(document: DocumentProp, formatters: Formatters): string {
  const {formatDate} = formatters;
  return document.isLoading ? '...' : `${document.name}, ${formatDate(document.timestamp ?? '')}`;
}
/**
 * Keeps the previously-shown document label visible while a newly-selected document is still loading.
 *
 * When switching documents the data query keys off the new `documentId`, so `name`/`timestamp` are
 * briefly `undefined`; without this the breadcrumb's document segment collapses for a frame (it only
 * renders when `name` is present), producing a visible "flash" back to the hospitalization and back.
 * Reusing the last known label until the new one resolves removes that flash (PDH-1947).
 */
export function useStableDocumentBreadcrumb<
  T extends {isLoading: boolean; id: string | number | undefined; name: string | undefined; timestamp: string | undefined}
>(document: T | undefined): T | undefined {
  const lastRef = React.useRef<{name: string; timestamp: string | undefined} | undefined>(undefined);
  if (!document?.id) {
    // no document selected (e.g. encounter view) — drop any retained label
    lastRef.current = undefined;
    return document;
  }
  if (document.name) {
    // document loaded — remember its label for the next switch
    lastRef.current = {name: document.name, timestamp: document.timestamp};
    return document;
  }
  // document selected but not loaded yet — reuse the last shown label so the segment doesn't collapse
  if (lastRef.current) {
    return {...document, isLoading: false, name: lastRef.current.name, timestamp: lastRef.current.timestamp};
  }
  return document;
}
export const PatientEncounterDocumentBreadcrumbs: React.FC<Props> = (props) => {
  const {patient, encounter} = props;
  const document = useStableDocumentBreadcrumb(props.document);
  const formatters = useFormatters();
  const intl = useIntl();
  const items: BreadcrumbItem[] = [];
  // patient
  if (patient?.id && patient?.name) {
    items.push({
      iconName: 'haidiUserIconMultiColor',
      iconViewBox: '0 0 16 16',
      text: formatPatientBreadcrumb(patient, intl),
      url: `/patients/${patient.id}`,
    });
  }
  // encounter
  const ESCORT_ENCOUNTER_COLORS = {
    primaryColor: '#FFDACA',
    secondaryColor: '#FF8717',
  };
  const DELETED_ENCOUNTER_COLORS = {
    primaryColor: '#FFD6D6',
    secondaryColor: '#FF5353',
  };
  if (encounter?.id && encounter?.orgShortName) {
    const iconMap: Record<EncounterEventType, IconDef> = {
      OUTPATIENT: {iconName: 'haidiStethoscopeIcon', iconViewBox: '0 0 12 12'},
      HOSPITALIZATION: {
        iconName: 'haidiBedIconMultiColor',
        iconViewBox: '0 0 17 12',
      },
    };
    items.push({
      ...(iconMap?.[encounter.encounterType ?? 'HOSPITALIZATION'] ?? iconMap.HOSPITALIZATION),
      ...(encounter.isEscort ? ESCORT_ENCOUNTER_COLORS : {}),
      ...(encounter.isDeleted ? DELETED_ENCOUNTER_COLORS : {}),
      text: formatEncounterBreadcrumb(encounter, formatters),
      url: `/encounter/${encounter.id}`,
    });
  }
  // document
  if (document?.id && document?.name) {
    items.push({
      ...(encounter?.id
        ? {
            iconName: 'haidiDocumentIconMultiColor2',
            iconViewBox: '0 0 17 16',
            url: `/encounter/${encounter.id}/document/${document.id}`,
          }
        : {
            iconName: 'haidiLabIconMultiColor',
            iconViewBox: '0 0 16 16',
            url: `/document/${document.id}`,
          }),
      text: formatDocumentBreadcrumb(document, formatters),
    });
  }
  return <LightBreadcrumbs items={items} />;
};
